// TMOS Issue Management Page
// Date: November 15, 2025
// Purpose: User-facing issue management page with tabbed interface

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IssuePool } from '@/components/issues/IssuePool';
import { IssueOverview } from '@/components/issues/IssueOverview';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ExclamationTriangleIcon, PlusIcon } from '@heroicons/react/24/outline';

export const IssueManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'pool'>('overview');

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="p-4">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="w-5 h-5 text-blue-600" />
                <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
                  {t('issues.title') || 'Issue Management'}
                </h1>
              </div>
              {activeTab === 'overview' && (
                <button
                  type="button"
                  onClick={() => navigate('/issues/create')}
                  className="inline-flex items-center justify-center px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] text-sm font-medium rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('issues.raiseIssue.button') || 'Raise Issue'}
                </button>
              )}
              {activeTab === 'pool' && (
                <button
                  type="button"
                  onClick={() => navigate('/issues/create')}
                  className="inline-flex items-center justify-center px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] text-sm font-medium rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('common.create')}
                </button>
              )}
            </div>
            <p className="text-xs text-[var(--color-secondary-text)] ml-7">
              {t('issues.subtitle') || 'Track and manage issues across the platform'}
            </p>
          </div>

          <div className="mt-6">
            <div className="border-b border-[var(--color-border)]">
              <nav className="-mb-px flex space-x-8 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'overview'
                      ? 'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)]'
                      : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
                  }`}
                >
                  {t('issues.overview') || 'Overview'}
                </button>
                <button
                  onClick={() => setActiveTab('pool')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'pool'
                      ? 'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)]'
                      : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
                  }`}
                >
                  {t('issues.pool') || 'Issue Pool'}
                </button>
              </nav>
            </div>
          </div>

          <div className="mt-6">
            {activeTab === 'overview' && (
              <IssueOverview />
            )}

            {activeTab === 'pool' && (
              <IssuePool />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


